import React, {useCallback, useState} from "react";
import {useTranslation} from "react-i18next";
import {get, isEmpty} from "lodash";
import AnimateHeight from "react-animate-height";
import styled from "@emotion/styled";
import {ISubLinkItem} from "data/types/navigation";
import iconNavShrink from "assets/img/icons/icon-nav-shrink.png";
import iconNavExpand from "assets/img/icons/icon-nav-expand.png";
import {useViewController} from "data/services/locator";
import type {IAuthController} from "views/controllers/auth/auth.controller";
import {Bindings} from "data/constants/bindings";
import {GAME_ZONE_URL} from "data/constants";
import {PrimaryButton} from "views/components/common";
import {Exist} from "views/components/Exist";
import {NavLink} from "react-router-dom";

const ItemWrapper = styled.div`
	margin-top: 2px;
	list-style: none;
`;

const ItemTitle = styled.div<{isOpen: boolean; $hideImage?: boolean}>`
	display: block;
	text-transform: uppercase;
	font-family: Khand, sans-serif;
	letter-spacing: 0.1em;
	width: 100%;
	line-height: 1;
	font-weight: 500;
	padding: 12px 10px 12px 20px;
	color: #fff;
	z-index: 2;
	font-size: 18px;
	background-color: #2b2b2b;
	transition: all 0.3s ease-out;

	${({$hideImage, isOpen}) =>
		!$hideImage &&
		`
		background-image: url(${isOpen ? iconNavShrink : iconNavExpand});
		background-repeat: no-repeat;
		background-position: right 10px center;
	`}
	&:active {
		background-color: ${({theme}) => theme.background.mobileLinkActive};
	}
`;

const SubLinks = styled.div``;

const SubLinksItem = styled.div`
	margin-top: 2px;
	list-style: none;
	overflow-wrap: break-word;

	img {
		width: 100%;
	}
`;

const Link = styled(NavLink)<{isOpen?: boolean; $withImage?: boolean}>`
	box-shadow: 0 2px #2b2b2b;
	margin-left: 30px;
	margin-right: 77px;
	width: 80%;
	padding: 12px 10px 12px 0;

	display: block;
	text-decoration: none;
	text-transform: uppercase;
	font-family: Khand, sans-serif;
	letter-spacing: 0.1em;
	line-height: 1;
	font-weight: 500;

	${({$withImage, isOpen}) =>
		$withImage &&
		`
		background-image: url(${isOpen ? iconNavShrink : iconNavExpand});
		background-repeat: no-repeat;
		background-position: right 10px center;
	`}
`;

const LinkExternal = styled.a<{isOpen?: boolean; $withImage?: boolean}>`
	box-shadow: 0 2px #2b2b2b;
	margin-left: 30px;
	margin-right: 77px;
	width: 80%;
	padding: 12px 10px 12px 0;

	display: block;
	text-decoration: none;
	text-transform: uppercase;
	font-family: Khand, sans-serif;
	letter-spacing: 0.1em;
	line-height: 1;
	font-weight: 500;

	${({$withImage, isOpen}) =>
		$withImage &&
		`
		background-image: url(${isOpen ? iconNavShrink : iconNavExpand});
		background-repeat: no-repeat;
		background-position: right 10px center;
	`}
`;

const WrapperForNav = styled.div`
	display: flex;
	flex-direction: column;

	a {
		display: block;
		margin: 12px 10px 12px 25px;
	}
`;

export interface IMobileItemLink {
	title: string;
	linkTitle?: string;
	urls?: ISubLinkItem[];
	url?: string;
	withLine?: boolean;
	image?: string;
	imageUrl?: string;
	defaultOpen?: boolean;
	isExternal?: boolean;
	toggleMainMenu?: () => void;
}

export const Item: React.FC<IMobileItemLink> = (menuItem) => {
	const {t} = useTranslation();
	const [isOpen, setIsOpen] = useState(() => get(menuItem, "defaultOpen", false));
	const {isAuthorized} = useViewController<IAuthController>(Bindings.AuthController);

	const toggleHandler = useCallback(() => {
		setIsOpen((prev) => !prev);
	}, []);

	return (
		<ItemWrapper>
			<ItemTitle isOpen={isOpen} onClick={toggleHandler} $hideImage={isEmpty(menuItem?.urls)}>
				{t(menuItem.title)}
			</ItemTitle>

			{menuItem.urls && (
				<AnimateHeight height={isOpen ? "auto" : 0}>
					<SubLinks>
						<Link to={menuItem.url || ""} onClick={menuItem.toggleMainMenu}>
							{t(menuItem.linkTitle || menuItem.title)}
						</Link>

						{menuItem.urls.map((el) =>
							el.isPrivate && !isAuthorized ? null : (
								<SubItem
									key={el.title}
									{...el}
									toggleMainMenu={menuItem.toggleMainMenu}
								/>
							)
						)}

						<Exist when={!isAuthorized}>
							<WrapperForNav>
								<a
									href={`${GAME_ZONE_URL}login?redirectUri=${window.location.pathname}`}
									className={"link-nav"}>
									<PrimaryButton
										style={{background: "#00FFFF", borderColor: "#00FFFF"}}>
										{t("sub_header.login", "Login")}
									</PrimaryButton>
								</a>
								<a
									href={`${GAME_ZONE_URL}registration?redirectUri=${window.location.pathname}`}
									className={"link-nav"}>
									<PrimaryButton>
										{t("sub_header.registration", "Registration")}
									</PrimaryButton>
								</a>
							</WrapperForNav>
						</Exist>

						{menuItem.image && <SubItemImage {...menuItem} />}
					</SubLinks>
				</AnimateHeight>
			)}
		</ItemWrapper>
	);
};

const ChildSubLinks = styled(SubLinks)`
	margin: 0 10px;
`;

const SubItem: React.FC<IMobileItemLink> = (el) => {
	const {t} = useTranslation();
	const [isOpen, setIsOpen] = useState(false);

	const toggleHandler = useCallback(() => {
		setIsOpen((prev) => !prev);
	}, []);

	if (!el.urls) {
		return (
			<SubLinksItem>
				<Exist when={Boolean(el.isExternal)}>
					<LinkExternal href={el.url} target={"_blank"}>
						{t(el.title)}
					</LinkExternal>
				</Exist>
				<Exist when={!el.isExternal}>
					<Link to={el.url || ""} onClick={el.toggleMainMenu}>
						{t(el.title)}
					</Link>
				</Exist>
			</SubLinksItem>
		);
	}

	return (
		<ItemWrapper>
			<LinkExternal isOpen={isOpen} $withImage onClick={toggleHandler}>
				{t(el.title)}
			</LinkExternal>

			<AnimateHeight height={isOpen ? "auto" : 0}>
				<ChildSubLinks>
					{el.urls.map((subMenu) => (
						<SubItem
							key={subMenu.title}
							{...subMenu}
							toggleMainMenu={el.toggleMainMenu}
						/>
					))}

					{el.image && <SubItemImage {...el} />}
				</ChildSubLinks>
			</AnimateHeight>
		</ItemWrapper>
	);
};

const SubItemImage: React.FC<IMobileItemLink> = (el) => (
	<SubLinksItem>
		<LinkExternal href={el.imageUrl}>
			<img src={el.image} alt="" />
		</LinkExternal>
	</SubLinksItem>
);
